/**
 * `insight.detected` event payload — the ambient analysis agent's
 * customer-facing contract.
 *
 * Emitted once per finding when it is first created (never on the
 * re-detections that bump `occurrence_count`, or the customer would be
 * told the same thing every week until it resolves). Rides the existing
 * webhook outbox, so it reaches email, Slack, Discord and generic
 * endpoints with no new delivery machinery.
 *
 * === Copy rules, which are part of the contract ===
 *
 * This payload is rendered straight into an email a customer did not
 * ask for. Two constraints follow, and both are load-bearing:
 *
 *  1. **Never assert causation.** We score how hard a movement is to
 *     explain away, not what caused it. Every phrasing here is "this
 *     lines up with" rather than "this was caused by". A confident
 *     wrong attribution is the failure mode that would sink the whole
 *     feature.
 *  2. **No internal identifiers.** No `aud_…`, no `site-…`, no query
 *     strings, no pipe names. Page paths and vendor names are fine —
 *     those are the customer's own URLs and public brands.
 *
 * Plan of record: `plans/ambient-analysis/` (README, "What this is").
 */
import { z } from 'zod';
import { FINDING_DIRECTIONS, FINDING_KINDS } from './ambient-analysis.js';

export const INSIGHT_DETECTED_EVENT = 'insight.detected' as const;

/**
 * One supporting number, already formatted for display by the producer.
 *
 * Deliberately pre-formatted rather than raw: the renderer must not
 * have to know that `cls` wants 2 decimals and `lcp` wants none, and a
 * Slack adapter and an email template must not drift on rounding.
 */
export const InsightMetricLineSchema = z.object({
  /** Human label, e.g. "LCP" or "Good LCP share". */
  label: z.string(),
  /** Current value, formatted — e.g. "3.4s", "62%". */
  value: z.string(),
  /** Baseline value, formatted the same way. */
  baseline: z.string(),
  /** Signed, human-readable delta — e.g. "+36%", "-25 points". */
  change: z.string(),
});

export const InsightDetectedEventDataSchema = z.object({
  finding_uuid: z.string(),
  site_uuid: z.string(),
  /** Display name for the site, or its URL when no name is set. */
  site_label: z.string(),
  kind: z.enum(FINDING_KINDS),
  direction: z.enum(FINDING_DIRECTIONS),
  /** Deterministic one-liner. Becomes the subject. */
  title: z.string(),
  /**
   * Plain-English body, one or two sentences. Written by the producer
   * (templated today; the deferred LLM narrator replaces it). Must
   * read as an observation, not an instruction.
   */
  summary: z.string(),
  /** 0-100. Surfaced as words, never as a number — see `confidenceLabel`. */
  proof_score: z.number(),
  high_confidence: z.boolean(),
  /** Complete UTC days the finding covers. */
  window_start: z.string(),
  window_end: z.string(),
  /** Consecutive days the movement held. */
  persistence_days: z.number(),
  /** Up to ~3 supporting numbers. */
  metrics: z.array(InsightMetricLineSchema),
  /**
   * What lines up with the movement, phrased for display — e.g.
   * "the deploy of a1b2c3d on main" or "a new TikTok script". Empty
   * when nothing identifiable coincided, which is a legitimate and
   * common state.
   */
  candidates: z.array(z.string()),
  /**
   * The specific deploy, when the best candidate is one and its marker
   * carries metadata. Lets the template render a real, linkable
   * reference — "d4f7a2c on main by phil" — instead of gesturing at
   * "a deploy". Absent for non-deploy findings and for markers with no
   * commit info.
   *
   * Deliberately NOT the place for commit messages or diffs: reading
   * commit contents needs the GitHub installation token and belongs to
   * the deferred narrator/investigator tiers, not a webhook payload.
   */
  deploy: z
    .object({
      short_sha: z.string().nullable(),
      branch: z.string().nullable(),
      deployer: z.string().nullable(),
      url: z.string().nullable(),
      deployed_on: z.string(),
    })
    .optional(),
  /** Independent datasets that agree — e.g. ["Chrome UX Report"]. */
  corroboration: z.array(z.string()),
  /** Share of the site's traffic affected, 0-1. */
  share_of_traffic: z.number(),
  /** Deep link to the surface that proves it. */
  view_url: z.string(),
});

export type InsightMetricLine = z.infer<typeof InsightMetricLineSchema>;
export type InsightDetectedEventData = z.infer<typeof InsightDetectedEventDataSchema>;

/**
 * Confidence as words, never a number.
 *
 * "Proof score 68" invites a conversation about the scoring function
 * that we would lose — the number is an internal ranking device, not a
 * claim we can defend to a customer. The calibration surface shows the
 * digits; the customer gets a hedge that matches how sure we are.
 */
export function confidenceLabel(data: InsightDetectedEventData): string {
  return data.high_confidence ? 'Strong evidence' : 'Worth a look';
}

/**
 * Subject line: site first, then the finding.
 *
 * The site is non-negotiable — an org watching five sites cannot
 * triage "LCP improved 31% for mobile visitors" without opening the
 * mail, and an inbox search for the site name must find these. No
 * product prefix beyond that: "[Clickweave] Insight detected" is the
 * kind of subject people filter away, and the title is already a
 * complete sentence.
 */
export function insightSubject(data: InsightDetectedEventData): string {
  return `${data.site_label}: ${data.title}`;
}
